/**
 * Modal — a centred dialog with a backdrop.
 *
 * MODALS ARE SURPRISINGLY EASY TO GET WRONG. This one handles the things
 * beginners usually miss:
 *   • Escape key closes it
 *   • Clicking the dark backdrop closes it, but clicking INSIDE does not
 *   • Background page scroll is locked while it is open (otherwise the page
 *     behind scrolls when you spin the wheel over the modal)
 *   • It is rendered through a React Portal, so it escapes any parent with
 *     `overflow: hidden` and always sits on top
 *   • It is announced to screen readers as a dialog
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

export function Modal({ isOpen, onClose, title, children, footer, size = 'md' }) {
  // Close on Escape, and lock background scrolling while open.
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Cleanup runs when the modal closes or unmounts — undo BOTH effects, or
    // the page stays permanently unscrollable after closing.
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title ?? 'Dialog'}
    >
      {/* Backdrop. Clicking it closes the modal. */}
      <div
        className="absolute inset-0 bg-ink/40 backdrop-blur-[2px] animate-rise"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel. stopPropagation stops an inside-click from bubbling up to the
          backdrop's onClick and closing the modal unexpectedly.
          On mobile it slides up from the bottom (a "sheet"); on desktop it is
          a centred card — the pattern users expect on each device. */}
      <div
        className={cn(
          'relative z-10 w-full rounded-t-2xl bg-surface shadow-pop animate-rise',
          'sm:rounded-2xl',
          SIZES[size],
        )}
        onClick={(event) => event.stopPropagation()}
      >
        {title && (
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <h2 className="text-base font-semibold text-ink">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close dialog"
              className="rounded-lg p-1.5 text-muted transition-colors hover:bg-line hover:text-ink"
            >
              <X size={20} />
            </button>
          </div>
        )}

        <div className="px-5 py-4">{children}</div>

        {footer && (
          <div className="flex justify-end gap-2 border-t border-line px-5 py-4">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default Modal;
